import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../UI/Modal';
import { Button } from '../UI/Button';
import { Skeleton } from '../UI/Skeleton';
import { MachhaCharacter } from '../../lib/machhaAsset';
import { albumsApi } from '../../api/albums';
import { Album } from '../../types';
import styles from './AddToAlbumSheet.module.css';

interface AddToAlbumSheetProps {
  isOpen: boolean;
  onClose: () => void;
  eventId: string;
  photoIds: string[];
  onAdded: (albumId: string, count: number) => void;
}

export function AddToAlbumSheet({ isOpen, onClose, eventId, photoIds, onAdded }: AddToAlbumSheetProps) {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [loading, setLoading] = useState(false);
  const [addingTo, setAddingTo] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!isOpen || initialized) return;
    setLoading(true);
    albumsApi
      .list(eventId)
      .then((r) => {
        setAlbums(r.albums);
        setInitialized(true);
      })
      .catch(() => toast.error('Failed to load albums'))
      .finally(() => setLoading(false));
  }, [isOpen, initialized, eventId]);

  // Reset when closed
  useEffect(() => {
    if (!isOpen) setInitialized(false);
  }, [isOpen]);

  const handleAdd = async (album: Album) => {
    setAddingTo(album._id);
    try {
      await albumsApi.addPhotos(album._id, photoIds);
      onAdded(album._id, photoIds.length);
      toast.success(`${photoIds.length} photo${photoIds.length !== 1 ? 's' : ''} added to "${album.name}"`);
      onClose();
    } catch {
      toast.error('Failed to add photos to album');
    } finally {
      setAddingTo(null);
    }
  };

  const title = `Add ${photoIds.length} photo${photoIds.length !== 1 ? 's' : ''} to album`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="sm">
      <div className={styles['content']}>
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className={styles['skeleton-row']}>
              <Skeleton className={styles['skeleton-name-bar']} />
              <Skeleton className={styles['skeleton-button-bar']} />
            </div>
          ))
        ) : albums.length === 0 ? (
          <div className={styles['empty-state']}>
            <MachhaCharacter state="Empty" size={80} />
            <p className={styles['empty-title']}>
              No albums for this event
            </p>
            <p className={styles['empty-description']}>
              Create an album from the Albums tab in this event to get started.
            </p>
          </div>
        ) : (
          albums.map((album) => (
            <div
              key={album._id}
              className={styles['album-row']}
            >
              <div className={styles['album-info']}>
                <p className={styles['album-name']}>
                  {album.name}
                </p>
                {album.description && (
                  <p className={styles['album-description']}>
                    {album.description}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="secondary"
                loading={addingTo === album._id}
                disabled={addingTo !== null}
                onClick={() => handleAdd(album)}
              >
                <Plus className={styles['icon-plus-add']} />
                Add
              </Button>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}
